/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
// import PropTypes from 'prop-types'
import {
    Box,
    Text,
    // Divider,
    SimpleGrid,
    useMultiStyleConfig,
    // Select as ChakraSelect,
    Heading,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Link,
    createStylesContext,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePage} from '@salesforce/commerce-sdk-react'
import SocialIcons from '../social-icons'
import {useIntl} from 'react-intl'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useSelectRoleContext} from '@consteed/frontend/hooks/use-select-role-modal'
import LinksList from '@consteed/overrides/components/links-list'
import useTracking from '@consteed/frontend/hooks/use-tracking'
import {APP_STORE_LINK_ANDROID, APP_STORE_LINK_IOS} from '@consteed/overrides/constants'
// import styled from '@emotion/styled'

const [StylesProvider, useStyles] = createStylesContext('Footer')
const Footer = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Footer')
    const intl = useIntl()
    const lang = intl.locale.split('-')
    const {data: page} = usePage({parameters: {pageId: 'footer'}})
    const {onOpen: onSelectRoleOpen} = useSelectRoleContext()
    const [firstLinkColumn, setFirstLinkColumn] = React.useState([])
    const [secondLinkColumn, setSecondLinkColumn] = React.useState([])
    const [showNewsletterSignUp, setShowNewsletterSignUp] = React.useState(false)
    const {eventTracking} = useTracking()

    // NOTE: this is a workaround to fix hydration error, by making sure that the `option.selected` property is set.
    // For some reason, adding some styles prop (to the option element) prevented `selected` from being set.
    // So now we add the styling to the parent element instead.
    // const Select = styled(ChakraSelect)({
    //     // Targeting the child element
    //     option: styles.localeDropdownOption
    // })

    React.useEffect(() => {
        if (page?.regions) {
            const firstRegion = page.regions.find((region) => {
                return region.id === 'first'
            })

            const secondRegion = page.regions.find((region) => {
                return region.id === 'second'
            })

            const convertLinkRegion = (region) => {
                return region?.components?.[0]?.regions?.[0]?.components
                    ?.map((link) => {
                        let linkListElement = {
                            text: link?.data?.linkText
                        }
                        switch (link?.data?.linkType) {
                            case 'Page':
                                if (!link?.data?.page) {
                                    return null
                                }
                                linkListElement.href = '/page-viewer/' + link?.data?.page
                                break
                            case 'Product':
                                if (!link?.data?.product) {
                                    return null
                                }
                                linkListElement.href = '/product/' + link?.data?.product
                                break
                            case 'Category':
                                if (!link?.data?.category) {
                                    return null
                                }
                                linkListElement.href = '/category/' + link?.data?.category
                                break
                            case 'External':
                                if (!link?.data?.externalUrl) {
                                    return null
                                }
                                // TODO LinksList currently does not handle external URLs correctly
                                linkListElement.href = link?.data?.externalUrl
                                break
                            case 'Static':
                                if (!link?.data?.staticResource) {
                                    return null
                                }
                                // TODO LinksList currently does not handle static URLs correctly
                                linkListElement.href = link?.data?.staticResource
                                break
                            case 'Blog':
                                // currently there is no special blog logic so the page designer page blog is used
                                linkListElement.href = '/page-viewer/blog'
                                break
                            case 'ContactUs':
                                linkListElement.href = '/contact'
                                break
                            case 'SelectRole':
                                linkListElement.href = '#'
                                linkListElement.onClick = (e) => {
                                    e.preventDefault()
                                    onSelectRoleOpen({isEdit: true})
                                }
                                break
                            default:
                                return null
                        }
                        return linkListElement
                    })
                    .filter((linkListElement) => linkListElement)
            }

            if (firstRegion) {
                const firstColumnLinks = convertLinkRegion(firstRegion)
                if (firstColumnLinks) {
                    setFirstLinkColumn(firstColumnLinks)
                }
            }

            if (secondRegion) {
                const secondColumnLinks = convertLinkRegion(secondRegion)

                if (secondColumnLinks) {
                    setSecondLinkColumn(secondColumnLinks)
                }
            }
        }
        if (page?.data?.showNewsletterSignUp) {
            setShowNewsletterSignUp(true)
        } else {
            setShowNewsletterSignUp(false)
        }
    }, [page])

    return (
        <Box as="footer" {...styles.container} {...otherProps}>
            <Box {...styles.content}>
                <StylesProvider value={styles}>
                    <SimpleGrid columns={{lg: 2}} spacing={3}>
                        <Box maxWidth="35rem">
                            <Box mb={'50px'}>
                                <Link href={'./'}>
                                    <Image
                                        src={getAssetUrl(
                                            'static/img/global/logoOrangeConsteed.png'
                                        )}
                                        alt={'Consteed Logo'}
                                    />
                                </Link>
                            </Box>
                            <Box>{showNewsletterSignUp && <Subscribe mb={8} />}</Box>
                        </Box>
                        <SimpleGrid columns={{md: 3}} spacing={3}>
                            <LinksList
                                mb={8}
                                {...styles.linkList}
                                links={firstLinkColumn}
                                linkTextStyle={{
                                    base: 'bookSmall',
                                    md: 'bookLarge',
                                    xl: 'bookXlarge'
                                }}
                                variant={'footer'}
                            />
                            <LinksList
                                mb={8}
                                {...styles.linkList}
                                links={secondLinkColumn}
                                linkTextStyle={{
                                    base: 'bookSmall',
                                    md: 'bookLarge',
                                    xl: 'bookXlarge'
                                }}
                                variant={'footer'}
                            />
                            <Box>
                                <Link
                                    href={APP_STORE_LINK_ANDROID}
                                    target={'_blank'}
                                    display={'flex'}
                                    justifyContent={'center'}
                                    mb={8}
                                    onClick={() => {
                                        try {
                                            eventTracking('appstore', {
                                                OS: 'Android',
                                                source: 'footer'
                                            })
                                        } catch (error) {
                                            // catch tracking errors to make sure the app works even if tracking fails
                                        }
                                    }}
                                >
                                    <Image
                                        width={'202px'}
                                        alt={intl.formatMessage({
                                            defaultMessage: 'Get it on Google Play',
                                            id: 'footer.link.googlePlaystore'
                                        })}
                                        src={getAssetUrl(
                                            `static/img/global/google-play-badge_${lang[0]}.png`
                                        )}
                                        transform={lang[0] === 'en' ? 'scale(1.15)' : ''}
                                    />
                                </Link>
                                <Link
                                    href={APP_STORE_LINK_IOS}
                                    target={'_blank'}
                                    display={'flex'}
                                    justifyContent={'center'}
                                    mb={'8'}
                                    onClick={() => {
                                        try {
                                            eventTracking('appstore', {
                                                OS: 'iOS',
                                                source: 'footer'
                                            })
                                        } catch (error) {
                                            // catch tracking errors to make sure the app works even if tracking fails
                                        }
                                    }}
                                >
                                    <Image
                                        width={'202px'}
                                        alt={intl.formatMessage({
                                            defaultMessage: 'Download on the App Store',
                                            id: 'footer.link.appStore'
                                        })}
                                        src={getAssetUrl(
                                            `static/img/global/app-store-badge_${lang[0]}.svg`
                                        )}
                                    />
                                </Link>
                                <SocialIcons variant={'flex-start'} mb={8} />
                                <Text textStyle={'bookSmall'}>© 2023 Continental AG</Text>
                            </Box>
                        </SimpleGrid>
                    </SimpleGrid>
                </StylesProvider>
            </Box>
        </Box>
    )
}

export default Footer

const Subscribe = ({...otherProps}) => {
    const styles = useStyles()
    const intl = useIntl()
    const {eventTracking} = useTracking()
    const onClick = () => {
        // TODO implement newsletter signup process
        try {
            eventTracking('generate_lead', {
                currency: 'EUR',
                value: 0,
                lead_source: 'Newsletter'
            })
        } catch (error) {
            // catch tracking errors to make sure the app works even if tracking fails
        }
    }
    return (
        <Box {...styles.subscribe} {...otherProps}>
            <Heading
                {...styles.subscribeHeading}
                size={{base: 'mediumXlarge', xl: 'medium2Xlarge'}}
            >
                {intl.formatMessage({
                    id: 'footer.subscribe.heading.first_to_know',
                    defaultMessage: 'Subscribe to Newsletter'
                })}
            </Heading>

            <Box>
                <InputGroup {...styles.inputGroup}>
                    {/* Had to swap the following InputRightElement and Input
                        to avoid the hydration error due to mismatched html between server and client side.
                        This is a workaround for Lastpass plugin that automatically injects its icon for input fields.
                    */}
                    <InputRightElement {...styles.subscribeButtonContainer}>
                        <Button variant="footer" onClick={onClick}>
                            {intl.formatMessage({
                                id: 'footer.subscribe.button.sign_up',
                                defaultMessage: 'Subscribe'
                            })}
                        </Button>
                    </InputRightElement>
                    <Input
                        type="email"
                        placeholder={intl.formatMessage({
                            id: 'footer.subscribe.input.placeholder',
                            defaultMessage: 'Enter your email address'
                        })}
                        {...styles.subscribeField}
                    />
                </InputGroup>
            </Box>
        </Box>
    )
}

// const LegalLinks = ({variant}) => {
//     const intl = useIntl()
//     return (
//         <LinksList
//             links={[
//                 {
//                     href: '/',
//                     text: intl.formatMessage({
//                         id: 'footer.link.terms_conditions',
//                         defaultMessage: 'Terms & Conditions'
//                     })
//                 },
//                 {
//                     href: '/',
//                     text: intl.formatMessage({
//                         id: 'footer.link.privacy_policy',
//                         defaultMessage: 'Privacy Policy'
//                     })
//                 },
//                 {
//                     href: '/',
//                     text: intl.formatMessage({
//                         id: 'footer.link.site_map',
//                         defaultMessage: 'Site Map'
//                     })
//                 }
//             ]}
//             color="gray.200"
//             variant={variant}
//         />
//     )
// }
// LegalLinks.propTypes = {
//     variant: PropTypes.oneOf(['vertical', 'horizontal'])
// }
